import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  FormControl,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import _ from 'underscore';
import accounting from 'accounting';
import { titleize } from 'underscore.string';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import CargomaticModal from 'components/v2/modal';
import DoRequestChanges from 'components/modules/shipments/delivery-order/RequestChanges';
import { getLatestStatus } from 'utils/formatters/shipments';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
import { withTranslation, Translation } from 'react-i18next';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { sluggify } from 'utils/localization/utils';
import ShipmentStore from 'stores/ShipmentStore';
import {
  DistanceUnitFormatter,
  LoadDimensionFormatter,
  LoadUnitFormatter,
} from 'components/modules/master/formatters';
import { withLocaleContext } from 'providers/locale-settings-context';
import { getYards } from 'utils/YardAPIUtils';
import { getDocumentsV2, getSignedUrl } from 'utils/DocumentsAPIUtils';
import { getErrorFromResponse } from 'utils/graphQL/utils';
import OfferAccessorial from 'components/offers/offer-accessorial';
import { isHazMat } from 'helpers/stops';

import {
  canEditAppointmentWindow,
  calculatePalletSpaces,
} from 'helpers/shipments';

import { getFileIconClass, isProd } from 'helpers/helper_functions';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import AddAppointmentModal from 'components/ui/tailwind/modals/AddAppointmentModal';
import { showToast } from 'stores/ToastStore';
import { Disclosure } from '@headlessui/react';
import {
  ChevronRightIcon,
  ChevronUpIcon,
  PencilIcon,
} from '@heroicons/react/20/solid';
import Alert from 'components/ui/Alert';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import NextSteps from 'components/modules/shipments/next-steps/NextSteps';
import Stop from './Stop';
import ShipmentDetailDateItem from '../details/ShipmentDetailDateItem';
import EditReferenceNumbersModal from '../EditReferenceNumbersModal';

const TOOLTIPS = {
  hazMat: (
    <Tooltip id="hazMat">
      <Translation>
        {(t, { i18n: _i18n }) => <p>{t('SHIPMENT_HAZARDOUS_MATERIALS')}</p>}
      </Translation>
    </Tooltip>
  ),
  editReferences: (
    <Tooltip id="tooltip-edit-references">
      <Translation>
        {(t, { i18n: _i18n }) => <p>{t('CLICK_TO_EDIT_REF_NUMS')}</p>}
      </Translation>
    </Tooltip>
  ),
  references: (
    <Tooltip id="tooltip-route-details">
      <Translation>
        {(t, { i18n: _i18n }) => <p>{t('REF_NUMS_NOTE')}</p>}
      </Translation>
    </Tooltip>
  ),
};

function getTrackingId(props) {
  const job = props.job || {};
  const referenceNumbers = job.reference_numbers || [];
  return (referenceNumbers.find((n) => n.name === 'tracking_id') || {}).value;
}
class JobDetailCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: true,
      appointmentStop: null,
      showAddAppointmentModal: false,
      editingTrackingId: false,
      tracking_id: getTrackingId(props),
      showEditReferenceNumbersModal: false,
      expandReferenceNumbers: true,
      showRequestChangesModal: false,
      showConfirmDeclineModal: false,
      delivery_order_signed_link: null,
      yards: null,
      isCarrier: props.isCarrier,
    };
  }

  findErrorsAndShowToast = (response, operationName) => {
    const errorMessages = getErrorFromResponse(response);
    if (errorMessages) {
      showToast(
        `${operationName} Error`,
        `${operationName} failed with error: ${errorMessages}`,
        'error',
      );
    }

    return errorMessages;
  };

  async retrieveSignedLink() {
    const lastDeliveryOrderResult = await getDocumentsV2([this.props.job._id], {
      types: 'delivery_order',
      options: {
        order: {
          field: 'createdAt',
          direction: 'desc',
        },
        limit: 1,
      },
    });

    if (this.findErrorsAndShowToast(lastDeliveryOrderResult, 'Get Documents')) {
      return;
    }

    if (lastDeliveryOrderResult?.data?.getDocumentsV2?.documents?.length > 0) {
      const signedUrlResult = await getSignedUrl(
        lastDeliveryOrderResult.data.getDocumentsV2.documents[0].key,
      );

      if (this.findErrorsAndShowToast(signedUrlResult, 'Get SignedUrl')) {
        return;
      }

      const delivery_order_name =
        lastDeliveryOrderResult.data.getDocumentsV2.documents[0].name;

      if (signedUrlResult?.data?.getSignedUrl?.signedUrl) {
        const delivery_order_signed_link =
          signedUrlResult.data.getSignedUrl.signedUrl;
        this.setState({ delivery_order_signed_link, delivery_order_name });
      }
    }
  }

  async componentDidMount() {
    ShipmentStore.addDeliveryOrderStatusUpdateChangeListener(
      this.handleDeliveryOrderStatusUpdate,
    );
    this.retrieveSignedLink();

    if (!isProd()) {
      const {
        market: { market_id: marketId },
      } = this.props.job;
      const yardsResponse = await getYards(
        'nameInternal',
        'ascend',
        null,
        null,
        {
          id: null,
          marketId,
          nameInternal: null,
          status: null,
        },
      );

      this.setState({ yards: yardsResponse.data.getYards.yards });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.state.editingTrackingId) {
      return;
    }

    if (nextProps.job) {
      this.setState({
        tracking_id: getTrackingId(nextProps),
      });
    }
  }

  componentWillUnmount() {
    ShipmentStore.removeDeliveryOrderStatusUpdateChangeListener(
      this.handleDeliveryOrderStatusUpdate,
    );
  }

  onRequestModalHide = () => this.setState({ showRequestChangesModal: false });
  onRequestModalShow = () => this.setState({ showRequestChangesModal: true });

  onAcceptDeliveryOrder = () => {
    const body = { status: 'accepted' };
    if (!body) {
      return null;
    }
    ShipmentsActionCreators.updateDeliveryOrderStatus(this.props.job._id, body);
  };

  onDeclineDeliveryOrder = () => {
    const body = { status: 'declined' };
    if (!body) {
      return null;
    }
    ShipmentsActionCreators.updateDeliveryOrderStatus(this.props.job._id, body);
    this.setState({ showConfirmDeclineModal: false });
  };

  getLoads() {
    const {
      job: { loads },
    } = this.props;
    return loads || [];
  }
  getBoxes() {
    return this.getLoads().filter((l) => l.type === 'boxes');
  }
  getPallets() {
    return this.getLoads().filter((l) => l.type === 'pallets');
  }
  getLoad(field) {
    const loads = this.getLoads();
    const boxes = loads.filter((l) => l.type === 'boxes')[0];
    const pallets = loads.filter((l) => l.type === 'pallets')[0];

    if (field === 'boxes' && !boxes) {
      return null;
    }

    if (field === 'pallets' && !pallets) {
      return null;
    }

    if (field === 'boxes') {
      return boxes;
    }
    return pallets;
  }

  getPalletLoads() {
    const { t } = this.props;
    const palletLoads = this.getPallets();
    const totalPalletSpaces = calculatePalletSpaces(palletLoads);
    return (
      <div className="load-field">
        <p>{this.getFormattedLoadCount('pallets')}</p>
        <p>{this.getFormattedLoadDimensions('pallets')}</p>
        {SessionStore.isAdmin() && (
          <p>
            {totalPalletSpaces} {t('PALLET_SPACES')}
          </p>
        )}
      </div>
    );
  }

  getBoxLoads() {
    const { t } = this.props;
    const boxLoads = this.getBoxes();
    const totalPalletSpaces = calculatePalletSpaces(boxLoads);
    return (
      <div className="load-field">
        <p>{this.getFormattedLoadCount('boxes')}</p>
        <p>{this.getFormattedLoadDimensions('boxes')}</p>
        {SessionStore.isAdmin() && (
          <p>
            {totalPalletSpaces} {t('PALLET_SPACES')}
          </p>
        )}
      </div>
    );
  }

  getFormattedLoadCount(field) {
    const { t } = this.props;
    const load = this.getLoad(field);
    const quantity = (load && load.quantity) || 0;

    let type = '';
    if (field === 'boxes') {
      type = quantity ? t('BOX', { count: 2 }) : t('BOX', { count: 1 });
    } else if (field === 'pallets') {
      type =
        quantity > 0 ? t('PALLET', { count: 2 }) : t('PALLET', { count: 2 });
    }

    return `${accounting.formatNumber(quantity)} ${titleize(type)}`;
  }
  getFormattedLoadDimensions(field) {
    const load = this.getLoad(field);

    if (!load) {
      return null;
    }

    const { width, height, length, dimensional_units } = load;

    if (width > 0 && height > 0 && length > 0) {
      return (
        <LoadDimensionFormatter
          length={length}
          width={width}
          height={height}
          dimensional_units={dimensional_units}
        />
      );
    }
    return null;
  }

  getFormattedDistance() {
    const {
      job: { distance },
      localeData,
    } = this.props;
    if (!distance) {
      return null;
    }

    return (
      <DistanceUnitFormatter
        fromUnit="m"
        toUnit={localeData.distance_units}
        distance={distance}
      />
    );
  }

  hasAccessorials() {
    const {
      job: { accessorials },
    } = this.props;

    if (accessorials && Object.keys(accessorials).length > 0) {
      return true;
    }
    return false;
  }

  handleDeliveryOrderStatusUpdate = () => {
    const result = ShipmentStore.getDeliveryOrderUpdateStatus();
    if (result.action === 'declined' && result.status) {
      window.location.href = '/shipments';
    }
  };

  handleSaveShipmentDate = (field) => (value) => {
    const { job: shipment } = this.props;
    switch (field) {
      case 'estimated_arrival':
        ShipmentsActionCreators.updateEstimatedArrival(shipment, value);
        break;
      case 'arrival_date':
        ShipmentsActionCreators.updateArrivalDate(shipment, value);
        break;
      case 'discharge_date':
        ShipmentsActionCreators.updateDischargeDate(shipment, value);
        break;
      case 'last_free_date':
        ShipmentsActionCreators.updateLastFreeDate(shipment, value);
        break;
      default:
    }
  };

  handleWindowSave = (updates) => {
    const { job } = this.props;

    const { appointmentStop: stop } = this.state;

    const updatedStop = {
      ...stop,
      ...updates,
    };

    ShipmentsActionCreators.updateShipmentStopWindow(job, updatedStop, updates);
    this.setState({
      showAddAppointmentModal: false,
      appointmentStop: null,
    });
  };

  handleEditAppointmentWindow = (stop) => {
    this.setState({
      showAddAppointmentModal: true,
      appointmentStop: stop,
    });
  };

  handleCancelWindowEdit = () => {
    this.setState({
      showAddAppointmentModal: false,
      appointmentStop: null,
    });
  };

  handleEditTrackingIdStart = () => this.setState({ editingTrackingId: true });
  handleEditTrackingIdChange = (e) =>
    this.setState({ tracking_id: e.target.value });
  handleEditTrackingIdCancel = () =>
    this.setState({
      tracking_id: getTrackingId(this.props),
      editingTrackingId: false,
    });
  handleEditTrackingIdSave = () => {
    const {
      job: { reference_numbers: currentReferenceNumbers },
    } = this.props;
    let trackingIdRefNum;
    const index = currentReferenceNumbers.findIndex(
      (r) => r.name === 'tracking_id',
    );

    let reference_numbers;
    if (index !== -1) {
      trackingIdRefNum = currentReferenceNumbers[index];
      trackingIdRefNum.value = this.state.tracking_id;
      reference_numbers = [
        ...currentReferenceNumbers.slice(0, index),
        trackingIdRefNum,
        ...currentReferenceNumbers.slice(index + 1),
      ];
    } else {
      trackingIdRefNum = {
        name: 'tracking_id',
        value: this.state.tracking_id,
      };

      reference_numbers = [...currentReferenceNumbers, trackingIdRefNum];
    }

    ShipmentsActionCreators.updateReferenceNumbers(
      this.props.job,
      reference_numbers,
    );
    setTimeout(() => this.setState({ editingTrackingId: false }), 250);
  };

  handleEditReferenceNumbers = () =>
    this.setState({ showEditReferenceNumbersModal: true });
  handleCloseEditReferenceNumbersModal = () =>
    this.setState({ showEditReferenceNumbersModal: false });
  handleSaveReferenceNumbers = (referenceNumbers) => {
    ShipmentsActionCreators.updateReferenceNumbers(
      this.props.job,
      referenceNumbers,
    );
    this.handleCloseEditReferenceNumbersModal();
  };

  openRequestChanges = () => {
    const { t } = this.props;
    return (
      <CargomaticModal
        show
        showCloseButton
        onHide={this.onRequestModalHide}
        title={t('REQUEST_CHANGES')}
        location="middle"
        dialogClassName="modal-grid"
        body={
          <DoRequestChanges match={{ params: { id: this.props.job._id } }} />
        }
        footer={null}
      />
    );
  };

  renderNotes() {
    const {
      job: { notes },
      t,
    } = this.props;
    if (notes) {
      return (
        <div className="offer-misc-info">
          <h5>{titleize(t('NOTE', { count: 2 }))}</h5>
          <small>{notes}</small>
        </div>
      );
    }
    return null;
  }

  renderHazMat() {
    if (!isHazMat(this.props.job)) {
      return null;
    }
    const { t } = this.props;
    return (
      <section className="my-4">
        <Alert type="error" className="upload-doc">
          <span>{t('SHIPMENT_HAZARDOUS_MATERIALS')}</span>
        </Alert>
        <hr />
      </section>
    );
  }
  renderAccessorials() {
    if (!this.hasAccessorials()) {
      return null;
    }

    const {
      job: { accessorials },
      t,
    } = this.props;

    const mappedAcessorialImages = Object.keys(accessorials).map((key) => {
      const accessorial = accessorials[key];
      return <OfferAccessorial key={key} accessorial={accessorial} />;
    });

    return (
      <div className="offer-misc-info">
        <h5>{t('ACCESSORIAL', { count: 2 })}</h5>
        <div className="offer-accessorials">{mappedAcessorialImages}</div>
      </div>
    );
  }

  renderLoad() {
    const {
      job: { loads, weight, weight_units },
      t,
    } = this.props;

    const totalPalletSpaces =
      loads && loads.length ? calculatePalletSpaces(loads) : 0;

    return (
      <div className="load-field">
        <p>{this.getFormattedDistance()}</p>
        <p>
          <LoadUnitFormatter
            isTotalWeight
            weight={weight}
            weight_units={weight_units}
          />
        </p>
        {totalPalletSpaces &&
        totalPalletSpaces > 0 &&
        SessionStore.isAdmin() ? (
          <p>
            {t('TOTAL_PALLET_SPACE_WITH_VALUE', {
              totalPalletSpace: totalPalletSpaces,
            })}
          </p>
        ) : null}
      </div>
    );
  }

  renderEquipment() {
    const {
      job: { stops },
      t,
    } = this.props;

    const equipmentList = _.uniq(
      stops.reduce((equipments, stop) => {
        return equipments.concat(stop.equipments || []);
      }, []),
      false,
      (eq) => eq._id,
    );

    if (!equipmentList.length) {
      return null;
    }

    return (
      <div>
        <div className="flex flex-row px-2 pb-2 mb-3 text-sm font-bold border-b-2 border-solid text-grey-darker border-grey-light">
          <div className="flex-1 min-w-150px">{t('EQUIPMENT_ID')}</div>
          <div className="flex-1 min-w-150px">{titleize(t('TYPE'))}</div>
          <div className="flex-1 min-w-100px">{t('SIZE', { count: 1 })}</div>
        </div>
        <div>
          {equipmentList.map((equipment) => {
            return (
              <div
                className="flex flex-row px-2 pb-2 text-sm"
                key={equipment._id}
              >
                <span className="flex-1 min-w-150px">{`${equipment.equipment_id}`}</span>
                <span className="flex-1 min-w-150px">
                  {t(sluggify(equipment._type))}
                </span>
                <span className="flex-1 min-w-100px">
                  {equipment._equipmentSize}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderRefNumbers() {
    const { t, job } = this.props;
    const isDrayage = isIntermodalShipmentType(job);
    const reference_numbers = this.props.job.reference_numbers;

    const renderedReferenceNumbers = reference_numbers.map((refNum) => {
      if (refNum.name === 'tracking_id') {
        return this.renderTrackingId(refNum);
      }
      return (
        <div key={refNum.name}>
          <label className="mb-2 form-label">
            {t(sluggify(refNum.name), { count: 1 })}
          </label>
          <p>{refNum.value}</p>
        </div>
      );
    });

    const showTrackingIdForm =
      reference_numbers.findIndex((n) => n.name === 'tracking_id') === -1;
    const referenceNumberForm = showTrackingIdForm
      ? this.renderTrackingId()
      : null;
    return (
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <h4 className="ml-0 mr-1 text-lg">{this.props.t('REF')} #</h4>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.references}>
            <InformationCircleIcon className="w-5 h-5" />
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={TOOLTIPS.editReferences}
            style={{ display: 'inline-block' }}
          >
            <PencilIcon
              className="w-5 h-5 ml-3 cursor-pointer"
              onClick={this.handleEditReferenceNumbers}
            />
          </OverlayTrigger>
        </div>

        <div>
          {referenceNumberForm}
          {renderedReferenceNumbers}
        </div>
        <EditReferenceNumbersModal
          isIntermodalShipmentType={isDrayage}
          reference_numbers={this.props.job.reference_numbers}
          shipper={this.props.job.shipper}
          show={this.state.showEditReferenceNumbersModal}
          onSave={this.handleSaveReferenceNumbers}
          onClose={this.handleCloseEditReferenceNumbersModal}
          page="ShipmentDetails"
          isCarrier={this.props.isCarrier}
        />
      </div>
    );
  }

  renderTrackingId(refNum = {}) {
    const { t } = this.props;
    const { editingTrackingId } = this.state;

    const showReadonlyField = !editingTrackingId;
    const value = refNum.value || t('NONE');

    if (showReadonlyField) {
      return (
        <div key="tracking-id-readonly">
          <label className="mb-2 form-label">{t('TRACKING_ID')}</label>
          <p className="flex">
            <span>{value}</span>
            {SessionStore.isShipper() && (
              <i
                style={{ cursor: 'pointer', color: '#718DAC' }}
                className="ml-2 fa fa-pencil"
                onClick={this.handleEditTrackingIdStart}
              />
            )}
          </p>
        </div>
      );
    }

    return (
      <form className="form-inline" key="tracking-id-form">
        <div className="mb-4">
          <FormControl
            type="text"
            placeholder={t('ENTER_TRACKING_ID')}
            onChange={this.handleEditTrackingIdChange}
            value={this.state.tracking_id}
          />
          <ButtonGroup style={{ marginLeft: '0.5rem' }}>
            <Button
              bsStyle="primary"
              bsSize="small"
              style={{ marginRight: 0 }}
              disabled={!this.state.tracking_id}
              onClick={this.handleEditTrackingIdSave}
            >
              {titleize(t('SAVE'))}
            </Button>
            <Button bsSize="small" onClick={this.handleEditTrackingIdCancel}>
              {t('CANCEL')}
            </Button>
          </ButtonGroup>
        </div>
      </form>
    );
  }

  renderDriver() {
    if (!this.props.activeDriver) {
      return null;
    }
    const {
      activeDriver: { phone, first_name, last_name },
    } = this.props;

    const firstName = (first_name || '').trim();
    const lastName = (last_name || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      <div className="mb-6">
        <h4 className="mb-4 ml-0 text-xl">
          {titleize(this.props.t('DRIVER', { count: 1 }))}
        </h4>
        <p className="flex items-center">
          <i className="mr-2 text-grey-darker fa fa-truck" aria-hidden="true" />
          <span className="ml-2 text-sm">{fullName}</span>
        </p>
        <p className="flex">
          <i className="text-grey-darker fa fa-phone" aria-hidden="true" />
          <span className="ml-2 text-sm">{phone}</span>
        </p>
      </div>
    );
  }

  renderDates() {
    const { job: shipment } = this.props;

    if (!shipment || !shipment.order) {
      return null;
    }

    const hideDates = ['export', 'domestic'].includes(
      shipment.order.order_type,
    );
    if (hideDates) {
      return null;
    }

    const {
      estimated_arrival,
      arrival_date,
      discharge_date,
      last_free_date,
      rail_lfd_at_facility_estimated,
      rail_earliest_return,
      rail_cut_date,
    } = shipment;
    const { t } = this.props;
    return (
      <div className="">
        <h4 className="text-xl">{t('DATES')}</h4>
        <div className="flex flex-wrap -mx-4">
          <div className="w-full px-4 md:w-1/2">
            <ShipmentDetailDateItem
              title={t('ETA')}
              value={estimated_arrival}
              editable
              onSave={this.handleSaveShipmentDate('estimated_arrival')}
            />
            <ShipmentDetailDateItem
              title={t('ARRIVAL')}
              value={arrival_date}
              editable
              onSave={this.handleSaveShipmentDate('arrival_date')}
            />
          </div>
          <div className="w-full px-4 md:w-1/2">
            <ShipmentDetailDateItem
              title={t('DISCHARGE')}
              value={discharge_date}
              editable
              onSave={this.handleSaveShipmentDate('discharge_date')}
            />
            <ShipmentDetailDateItem
              title={t('LAST_FREE')}
              value={last_free_date}
              editable
              onSave={this.handleSaveShipmentDate('last_free_date')}
            />
            {rail_lfd_at_facility_estimated && (
              <ShipmentDetailDateItem
                title={t('LFD_AT_RAIL_FACILITY_ESTIMATED')}
                key="LFD At Rail Facility Estimated"
                value={rail_lfd_at_facility_estimated}
              />
            )}
            {rail_earliest_return && (
              <ShipmentDetailDateItem
                title={t('RAIL_EARLIEST_RETURN_DATE')}
                key="Rail Earliest Return Date"
                value={rail_earliest_return}
              />
            )}
            {rail_cut_date && (
              <ShipmentDetailDateItem
                title={t('RAIL_CUT_DATE')}
                key="Rail Cut Date"
                value={rail_cut_date}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  _canViewDeliveryOrderActions = () => {
    const isDrayage = this.props.job.shipment_type === 'drayage';
    // code of 60 is accepeted
    return isDrayage && getLatestStatus(this.props.job).status.code < 60;
  };

  renderDeliveryOrder = () => {
    if (this.props.isShipper) return null;
    const { t } = this.props;

    const extension = this.state.delivery_order_name
      ? this.state.delivery_order_name.split('.').pop().toUpperCase()
      : '';

    return (
      <div className="">
        <div>
          <h4 className="text-grey-darker">{t('DELIVERY_ORDER')}</h4>
          <div className="flex py-4 border-b border-solid border-grey-light">
            <div className="justify-center text-center">
              <a
                className="text-center no-underline"
                href={this.state.delivery_order_signed_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div>
                  <span className="text-4xl">
                    <i
                      className={`${getFileIconClass(extension)} pb-2 text-xl`}
                    />
                  </span>
                  <div>
                    <span>{t('DELIVERY_ORDER')}</span>
                  </div>
                </div>
              </a>
            </div>
            {this._canViewDeliveryOrderActions() && (
              <div className="px-8 py-4">
                <button
                  className="button button-green"
                  onClick={this.onAcceptDeliveryOrder}
                >
                  {t('ACCEPT')}
                </button>
                <button
                  className="mx-1 button button-yellow"
                  onClick={this.onRequestModalShow}
                >
                  {t('REQUEST_CHANGES')}
                </button>
                <button
                  className="button button-red"
                  onClick={() => {
                    this.setState({ showConfirmDeclineModal: true });
                  }}
                >
                  {t('DECLINE')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderFirstRow() {
    const {
      job: { order },
    } = this.props;
    const { t } = this.props;
    const shipperHideColumnProperties =
      this.props?.job?.shipper?.hide_column_properties ?? [];

    const showDriver =
      !this.props.isShipper || !shipperHideColumnProperties.includes('driver');
    return (
      <div className="flex flex-wrap -mx-4">
        <div className="w-full px-4 py-4 md:w-1/3">
          {this.renderRefNumbers()}
          {order && (
            <div className="text-sm">
              <label className="mb-2 form-label">{t('SSL_RR')}</label>
              <p>{order.steamship_or_railroad}</p>
            </div>
          )}
        </div>
        <div className="w-full px-4 py-4 md:w-2/3">
          {showDriver ? this.renderDriver() : null}
          {this.renderDates()}
        </div>
      </div>
    );
  }
  renderAddAppointmentModal() {
    if (!this.state.showAddAppointmentModal) {
      return null;
    }

    const { appointmentStop } = this.state;

    return (
      <AddAppointmentModal
        stop={appointmentStop}
        shipment={this.props.job}
        show={this.state.showAddAppointmentModal}
        onSave={this.handleWindowSave}
        onHide={this.handleCancelWindowEdit}
      />
    );
  }

  renderStops() {
    const { job, t } = this.props;
    const stops = _.sortBy(job.stops || [], (s) => s.sequence);

    const miles = this.getFormattedDistance();
    const appointmentEditable = canEditAppointmentWindow(job);
    const isDrayage = isIntermodalShipmentType(job);

    const yardName = (yardId) => {
      const { yards } = this.state;
      if (yards && yards.length) {
        return yards
          .filter((yard) => yard.id === yardId)
          .map((item) => {
            return SessionStore.isAdmin()
              ? item.nameInternal
              : item.nameExternal;
          });
      }

      return null;
    };

    return (
      <div className="-mx">
        <div className="flex mb-4">
          <h4 className="pr-1 ml-0 text-xl">{t('ROUTE')}</h4>
          <h5 className="my-auto">
            {' '}
            - {stops.length} {t('STOP', { count: 2 })} ({miles})
          </h5>
        </div>
        {stops.map((stop) => (
          <Stop
            key={stop._id}
            yardName={yardName(stop.yardId)}
            stop={stop}
            onEditAppointmentWindow={this.handleEditAppointmentWindow}
            shipment_type={job.shipment_type}
            appointmentEditable={isDrayage && appointmentEditable}
          />
        ))}
      </div>
    );
  }

  renderSteps() {
    const { job } = this.props;
    if (
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      isIntermodalShipmentType(job) &&
      !SessionStore.isShipper()
    ) {
      return <NextSteps shipment={job} />;
    }
    return null;
  }

  render() {
    const hasPalletLoads = this.getPallets().length > 0;
    const hasBoxLoads = this.getBoxes().length > 0;
    const loadHeaderStyle = {
      marginTop: '0.4em',
      display: 'flex',
      flexDirection: 'row',
    };
    const { job: _job, t } = this.props;
    return (
      <div className="offer-panel">
        <div className="panel-body offer-list-item">
          <Disclosure>
            <>
              {this.renderHazMat()}
              {this.renderSteps()}
              {!isProd() && this.renderDeliveryOrder()}
              {this.renderFirstRow()}
              {!isProd()
                ? this.state.yards && this.renderStops() // avoid text replacement
                : this.renderStops()}
              <div style={loadHeaderStyle} className="justify-between">
                <h4 className="mt-0 mb-4 ml-0 text-xl">{t('LOAD_DETAILS')}</h4>
                <Disclosure.Button className="offer-view-more">
                  {({ open }) => (
                    <>
                      {!open && <ChevronRightIcon className="w-5 h-5" />}
                      {open && <ChevronUpIcon className="w-5 h-5" />}
                    </>
                  )}
                </Disclosure.Button>
              </div>
              <Disclosure.Panel>
                <div className="offer-details">
                  <div className="offer-info">
                    {this.renderLoad()}
                    {hasPalletLoads && this.getPalletLoads()}
                    {hasBoxLoads && this.getBoxLoads()}
                  </div>
                  <div className="offer-info">{this.renderEquipment()}</div>
                  {this.renderAccessorials()}
                  {this.renderNotes()}
                </div>
              </Disclosure.Panel>
            </>
          </Disclosure>
        </div>
        <ConfirmModal
          show={this.state.showConfirmDeclineModal}
          title={t('CONFIRM_ACTION')}
          message={t('DECLINE_CONFIRMATION_COMMENT')}
          onHide={() => this.setState({ showConfirmDeclineModal: false })}
          onConfirm={this.onDeclineDeliveryOrder}
          hideButtonText={t('CANCEL')}
          affirmative={t('OK')}
        />

        {this.renderAddAppointmentModal()}
        {this.state.showRequestChangesModal && this.openRequestChanges()}
      </div>
    );
  }
}

JobDetailCard.propTypes = {
  isShipper: PropTypes.bool,
  isCarrier: PropTypes.bool,
  job: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    shipment_id: PropTypes.string.isRequired,
    shipment_type: PropTypes.string.isRequired,
    exposed_at: PropTypes.string.isRequired,
    pickup_after: PropTypes.string.isRequired,
    pickup_by: PropTypes.string.isRequired,
    reference_numbers: PropTypes.array,
    total_payable_rate: PropTypes.number.isRequired,
    deliver_after: PropTypes.string.isRequired,
    deliver_by: PropTypes.string.isRequired,
    pallets: PropTypes.arrayOf(
      PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        length: PropTypes.number.isRequired,
      }),
    ).isRequired,
    weight: PropTypes.number.isRequired,
    distance_in_miles: PropTypes.number.isRequired,
    origin: PropTypes.arrayOf(
      PropTypes.shape({
        city: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ),
    destination: PropTypes.arrayOf(
      PropTypes.shape({
        city: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ),
    stops: PropTypes.arrayOf(
      PropTypes.shape({
        location: PropTypes.arrayOf(
          PropTypes.shape({
            city: PropTypes.string.isRequired,
            name: PropTypes.string,
            address: PropTypes.string,
          }),
        ),
        loads: PropTypes.arrayOf(
          PropTypes.shape({
            weight: PropTypes.number,
            width: PropTypes.number,
            lenght: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
      }),
    ),
  }).isRequired,
  activeDriver: PropTypes.shape({
    fullName: PropTypes.string,
    phone: PropTypes.string,
  }),
};

JobDetailCard.defaultProps = {
  activeDriver: null,
};

export default withTranslation()(withLocaleContext(JobDetailCard));
